import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { IconButton, TextField, Typography, Divider } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SessionSchedulePicker } from './SessionSchedulePicker';

interface CreateGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (groupName: string, startNow: boolean, scheduledDate: Date | null) => void;
}

const IMMEDIATE = "startNow";
const SCHEDULED = "schedule";

const CreateGroupModal: React.FC<CreateGroupModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const [groupName, setGroupName] = useState('');
  const [immediateOrScheduled, setImmediateOrScheduled] = useState(IMMEDIATE);
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    onClose();
    setGroupName('');
    setImmediateOrScheduled(IMMEDIATE);
    setScheduledDate(null);
    setError(null);
  };

  const handleConfirm = () => {
    if (!groupName.trim()) {
      setError('Course name is required.');
      return;
    }

    
    onConfirm(groupName, immediateOrScheduled === IMMEDIATE, scheduledDate);

    setGroupName('');
    setImmediateOrScheduled(IMMEDIATE);
    setScheduledDate(null);
    setError(null);
  };

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogTitle>Create New Course</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ paddingTop: 0, width: '360px' }}>
        {/* --- Course Details Section --- */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600, marginTop: 1 }}>
          Course Details
        </Typography>
        <TextField
          helperText="Name must be 32 characters or less."
          autoFocus
          autoComplete="off"
          margin="dense"
          id="name"
          label="Course Name"
          type="text"
          fullWidth
          variant="standard"
          value={groupName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setGroupName(e.target.value);
            setError(null);
          }}
          error={!!error && !groupName.trim()}
        />

        <Divider sx={{ my: 2 }} />

        {/* --- Start Options Section --- */}
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Start Options
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Choose when you’d like this course to begin. You can also skip and decide later.
        </Typography>

          <SessionSchedulePicker
            scheduledDate={scheduledDate}
            onChange={(startNow, date) => {
              setImmediateOrScheduled(startNow ? IMMEDIATE : SCHEDULED);
              setScheduledDate(date);
            }}
            error={error && !groupName.trim() ? error : null}
            setError={setError}
            showDoNotStart
          />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>CANCEL</Button>
        <Button type="submit" onClick={handleConfirm}>CREATE</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGroupModal;
